import { ReactNode, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import { useNavigate } from 'react-router-dom'

import { config } from '@/config'
import { paths } from '@/paths'
import { AuthStrategy } from '@/lib/auth/strategy'
import { logger } from '@/lib/default-logger'
import { useUser } from '@/hooks/use-user'

export interface AuthenticationGuardProps {
	children: ReactNode
}

export function AuthenticationGuard({ children }: AuthenticationGuardProps) {
	const navigate = useNavigate()
	const { user, error, isLoading } = useUser()
	const [isChecking, setIsChecking] = useState<boolean>(true)

	const checkPermissions = async (): Promise<void> => {
		if (isLoading) {
			return
		}

		if (error) {
			setIsChecking(false)
			return
		}

		if (!user) {
			logger.debug('[AuthenticationGuard]: User is not logged in, redirecting to sign in')

			switch (config.auth.strategy) {
				case AuthStrategy.CUSTOM: {
					navigate(paths.auth.custom.signIn, { replace: true })
					return
				}
				case AuthStrategy.FRONTEGG_CUSTOM: {
					navigate(paths.auth.fronteggCustom.signIn, { replace: true })
					return
				}
				case AuthStrategy.AUTH0: {
					navigate(paths.auth.auth0.signIn, { replace: true })
					return
				}
				case AuthStrategy.FRONTEGG: {
					navigate(paths.auth.frontegg.signIn, { replace: true })
					return
				}
				default: {
					logger.error('[AuthenticationGuard]: Unknown auth strategy')
					return
				}
			}
		}

		setIsChecking(false)
	}

	useEffect(() => {
		checkPermissions().catch(() => {
			// noop
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
	}, [user, error, isLoading])

	if (isChecking) {
		return null
	}

	if (error) {
		return <Alert color="error">{error}</Alert>
	}

	return <>{children}</>
}
